import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const MenuContext = createContext();

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within MenuProvider');
  }
  return context;
};

export const MenuProvider = ({ children }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to menu items in real-time
  useEffect(() => {
    const q = query(collection(db, 'menu_items'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMenuItems(items);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching menu items:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Add new menu item
  const addMenuItem = async (itemData) => {
    try {
      const newItem = {
        ...itemData,
        isAvailable: itemData.isAvailable !== undefined ? itemData.isAvailable : true, // Default to available
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        totalOrders: 0,
        popularity: 0
      };

      const docRef = await addDoc(collection(db, 'menu_items'), newItem);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error adding menu item:', error);
      return { success: false, error };
    }
  };

  // Update menu item
  const updateMenuItem = async (itemId, updates) => {
    try {
      const itemRef = doc(db, 'menu_items', itemId);
      await updateDoc(itemRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating menu item:', error);
      return { success: false, error };
    }
  };

  // Delete menu item
  const deleteMenuItem = async (itemId) => {
    try {
      await deleteDoc(doc(db, 'menu_items', itemId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting menu item:', error);
      return { success: false, error };
    }
  };

  // Toggle availability
  const toggleAvailability = async (itemId, currentStatus) => {
    return updateMenuItem(itemId, { isAvailable: !currentStatus });
  };

  // Toggle all items in a category
  const toggleCategoryAvailability = async (category, newStatus) => {
    try {
      const categoryItems = menuItems.filter(item => item.category === category);
      const updates = categoryItems.map(item =>
        updateMenuItem(item.id, { isAvailable: newStatus })
      );
      await Promise.all(updates);
      return { success: true };
    } catch (error) {
      console.error('Error toggling category availability:', error);
      return { success: false, error };
    }
  };

  // Search menu items
  const searchMenuItems = (searchTerm) => {
    if (!searchTerm) return menuItems;
    const term = searchTerm.toLowerCase();
    return menuItems.filter(item =>
      item.name.toLowerCase().includes(term) ||
      item.category.toLowerCase().includes(term)
    );
  };

  // Filter by category
  const filterByCategory = (category) => {
    if (!category || category === 'all') return menuItems;
    return menuItems.filter(item => item.category === category);
  };

  // Get stats
  const getStats = () => {
    const totalItems = menuItems.length;
    const availableItems = menuItems.filter(item => item.isAvailable).length;
    const vegItems = menuItems.filter(item => item.isVeg).length;
    const nonVegItems = menuItems.filter(item => !item.isVeg).length;

    return {
      totalItems,
      availableItems,
      vegItems,
      nonVegItems
    };
  };

  const value = {
    menuItems,
    loading,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    toggleAvailability,
    toggleCategoryAvailability,
    searchMenuItems,
    filterByCategory,
    getStats
  };

  return (
    <MenuContext.Provider value={value}>
      {children}
    </MenuContext.Provider>
  );
};
